import { useState, useRef } from "react";
import { DS } from "../../lib/ui";

// FeuilleTraite — « c'est traité », et POURQUOI.
//
// Maquette du 2026-09-09 : « chaque message en attente a une coche. Elle ouvre
// une feuille qui demande pourquoi : j'ai répondu ailleurs · ça n'appelle pas
// de réponse · je m'en occupe plus tard. Le motif est demandé, jamais supposé
// — comme pour mes alertes. »
//
// ⚠️ POURQUOI LE MOTIF EST OBLIGATOIRE. Une alerte tue sans raison est
// indistinguable d'un bug six mois plus tard : on relit la veille, on la trouve
// silencieuse, et on ne sait pas si c'est parce que tout va bien ou parce que
// quelqu'un a cliqué. Le module qui les applique (lib/veille-silences.js) fait
// d'ailleurs voyager le motif AVEC l'alerte tue, pour cette raison exacte.
//
// ⚠️ ET UNE ALERTE TUE N'EST PAS UNE ALERTE SUPPRIMÉE. Elle reste comptée et
// rendue dans une liste à part. Si taire faisait disparaître, l'écran finirait
// par mentir.
//
// ⚠️ « JE M'EN OCCUPE PLUS TARD » EST MENSUEL, PAS PERMANENT. C'est la
// différence qui compte : « plus tard » veut dire « pas ce mois-ci », et
// l'alerte revient le mois suivant si l'oubli, lui, revient. Un « plus tard »
// permanent serait un oubli déguisé en décision.

const MOTIFS = [
  {
    cle: "repondu_ailleurs",
    texte: "J'ai répondu ailleurs",
    aide: "Par téléphone, par WhatsApp, en direct sur la plateforme.",
    // Définitif : ce fil-là a eu sa réponse, il n'en aura pas deux.
    periode: null,
  },
  {
    cle: "sans_reponse",
    texte: "Ça n'appelle pas de réponse",
    aide: "Un remerciement, un message automatique, rien à faire.",
    periode: null,
  },
  {
    cle: "plus_tard",
    texte: "Je m'en occupe plus tard",
    aide: "Se tait ce mois-ci, et revient le mois prochain si ça traîne encore.",
    // ⚠️ Le mois COURANT : le silence s'éteint tout seul au changement de mois.
    periode: "mois",
  },
];

/** Le mois courant à Paris — le silence « plus tard » porte dessus. */
function moisCourant() {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
  })
    .format(new Date())
    .slice(0, 7);
}

export default function FeuilleTraite({ ligne, onFermer, onTaire }) {
  const [enCours, setEnCours] = useState(false);
  const [echec, setEchec] = useState(null);
  // ⚠️ UNE RÉFÉRENCE, PAS L'ÉTAT — piège de la fermeture périmée. `setEnCours`
  // n'a pas encore pris effet quand deux clics tombent dans le même lot React :
  // les deux lisent `false` et les deux partent. Ici ce n'est pas seulement un
  // appel de trop — deux MOTIFS DIFFÉRENTS partiraient, et le second
  // ÉCRASERAIT le premier (l'upsert est sur famille+cle+periode). On
  // enregistrerait « ça n'appelle pas de réponse » là où Dimitri a cliqué
  // « j'ai répondu ailleurs ». Trouvé par un test.
  const enVol = useRef(false);
  if (!ligne) return null;

  async function choisir(m) {
    if (enVol.current) return;
    enVol.current = true;
    setEnCours(true);
    setEchec(null);
    try {
      await onTaire({
        famille: ligne.taire.famille,
        cle: ligne.taire.cible,
        periode: m.periode === "mois" ? moisCourant() : null,
        motif: m.texte,
      });
      onFermer();
    } catch (e) {
      // ⚠️ ON NE FERME PAS SUR UN ÉCHEC. Fermer donnerait l'impression que
      // c'est fait — et la ligne réapparaîtrait au prochain chargement sans
      // qu'on comprenne pourquoi.
      setEchec(
        "Je n'ai pas pu l'enregistrer. La ligne reste, réessayez dans un instant.",
      );
      enVol.current = false;
      setEnCours(false);
    }
  }

  return (
    <div
      data-testid="feuille-traite"
      role="dialog"
      aria-modal="true"
      aria-label="Pourquoi c'est traité"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(35,50,64,.42)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      onClick={onFermer}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(460px, 100%)",
          background: "#fff",
          borderRadius: "18px 18px 0 0",
          padding: "18px 18px 22px",
          fontFamily: DS.fontBody,
        }}
      >
        <p style={{ margin: "0 0 4px", fontSize: 13.5, fontWeight: 600 }}>
          Pourquoi c&rsquo;est traité ?
        </p>
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 11.5,
            color: DS.textSecondary || "#5c6773",
          }}
        >
          {ligne.titre}
        </p>

        {MOTIFS.map((m) => (
          <button
            key={m.cle}
            type="button"
            data-testid={`traite-${m.cle}`}
            disabled={enCours}
            onClick={() => choisir(m)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              minHeight: 52,
              padding: "9px 13px",
              marginBottom: 8,
              borderRadius: 12,
              border: "1px solid #e3e8ee",
              background: "#f8fafc",
              cursor: enCours ? "default" : "pointer",
              opacity: enCours ? 0.6 : 1,
              fontFamily: DS.fontBody,
            }}
          >
            <span style={{ display: "block", fontSize: 13, fontWeight: 600 }}>
              {m.texte}
            </span>
            <span
              style={{
                display: "block",
                fontSize: 11,
                color: DS.textSecondary || "#5c6773",
                marginTop: 2,
              }}
            >
              {m.aide}
            </span>
          </button>
        ))}

        {echec && (
          <p
            data-testid="traite-echec"
            style={{ margin: "6px 0 0", fontSize: 11.5, color: "#8A4B2A" }}
          >
            {echec}
          </p>
        )}

        <button
          type="button"
          data-testid="traite-annuler"
          onClick={onFermer}
          style={{
            display: "block",
            width: "100%",
            minHeight: 44,
            marginTop: 4,
            border: "none",
            background: "none",
            fontSize: 12,
            color: DS.textSecondary || "#5c6773",
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

export const _MOTIFS = MOTIFS;
export const _moisCourant = moisCourant;
